import { useRef, useState } from "react";
import Modal from "../../modal/Modal";
import { applyFieldImport, previewFieldImport } from "../../../utils/cdesApi";
import "./FieldAdmin.scss";

const detailText = (item) => {
    if (!item || typeof item !== "object") return "";
    const path = typeof item.path === "string" ? item.path.trim() : "";
    const message = typeof item.message === "string" ? item.message.trim() : "";
    if (path && message) return `${path}: ${message}`;
    return message || path;
};

const importErrorDetails = (error) => {
    const details = error?.cause?.errors;
    const items = Array.isArray(details) ? details.map(detailText).filter(Boolean) : [];
    const message = typeof error?.message === "string" ? error.message.trim() : "";
    return { items, message };
};

const stringKeys = (value) => (Array.isArray(value) ? value.filter((key) => typeof key === "string" && key.trim()) : []);

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const readFileText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error || new Error("The catalog file could not be read."));
        reader.readAsText(file);
    });

const ChangeGroup = ({ title, added, updated, unchanged }) => {
    const addedKeys = stringKeys(added);
    const updatedKeys = stringKeys(updated);
    const unchangedKeys = stringKeys(unchanged);
    const visibleUnchanged = unchangedKeys.length <= 8 ? unchangedKeys : [];
    return (
        <section className="cdes-import__group" aria-label={title}>
            <p className="cdes-import__heading">{title}</p>
            <div className="cdes-import__counts">
                <span className="cdes-grid__status cdes-grid__status--ok">{countLabel(addedKeys.length, "to add", "to add")}</span>
                <span className="cdes-grid__status">{countLabel(updatedKeys.length, "to update", "to update")}</span>
                <span className="cdes-grid__status cdes-grid__status--muted">{countLabel(unchangedKeys.length, "unchanged", "unchanged")}</span>
            </div>
            {addedKeys.length ? (
                <ul className="cdes-import__keys" aria-label={`${title} to add`}>
                    {addedKeys.map((key) => (
                        <li key={`add-${key}`}>
                            Add <span className="cdes-import__key">{key}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
            {updatedKeys.length ? (
                <ul className="cdes-import__keys" aria-label={`${title} to update`}>
                    {updatedKeys.map((key) => (
                        <li key={`update-${key}`}>
                            Update <span className="cdes-import__key">{key}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
            {visibleUnchanged.length ? (
                <ul className="cdes-import__keys" aria-label={`${title} unchanged`}>
                    {visibleUnchanged.map((key) => (
                        <li key={`same-${key}`}>{key}</li>
                    ))}
                </ul>
            ) : null}
        </section>
    );
};

const assignmentLabel = (keys) => (stringKeys(keys).length ? stringKeys(keys).join(", ") : "none");

const ImportPreview = ({ preview }) => {
    const replacements = Array.isArray(preview?.assignmentReplacements) ? preview.assignmentReplacements : [];
    return (
        <div>
            <ChangeGroup
                title="Document types"
                added={preview?.additions?.documentTypes}
                updated={preview?.updates?.documentTypes}
                unchanged={preview?.unchanged?.documentTypes}
            />
            <ChangeGroup
                title="Fields"
                added={preview?.additions?.fields}
                updated={preview?.updates?.fields}
                unchanged={preview?.unchanged?.fields}
            />
            <section className="cdes-import__group" aria-label="Assignment changes">
                <p className="cdes-import__heading">Assignment changes</p>
                {replacements.length ? (
                    <ul className="cdes-import__list" aria-label="Assignment changes">
                        {replacements.map((change, index) => {
                            const fieldKey = typeof change?.fieldKey === "string" ? change.fieldKey : "Unknown field";
                            return (
                                <li key={`${fieldKey}-${index}`}>
                                    <span className="cdes-import__key">{fieldKey}</span>
                                    <div className="cdes-import__change">
                                        <span>Added {assignmentLabel(change?.added)}</span>
                                        <span>Removed {assignmentLabel(change?.removed)}</span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p>No document-type assignments will be added or removed.</p>
                )}
            </section>
        </div>
    );
};

const keyList = (value) => stringKeys(value);

const ImportResult = ({ result }) => {
    const applied = result?.status === "applied";
    const failed = result?.failed && typeof result.failed === "object" ? result.failed : null;
    const failedMessage = typeof failed?.message === "string" ? failed.message.trim() : "";
    const failedKey = typeof failed?.key === "string" && failed.key.trim() ? failed.key.trim() : "";
    const failedKind = typeof failed?.kind === "string" && failed.kind.trim() ? failed.kind.trim() : "record";
    const savedTypes = keyList(result?.committed?.documentTypes);
    const savedFields = keyList(result?.committed?.fields);
    const skippedTypes = keyList(result?.notAttempted?.documentTypes);
    const skippedFields = keyList(result?.notAttempted?.fields);
    return (
        <div className={`cdes-import__result ${applied ? "cdes-import__result--ok" : "cdes-import__result--partial"}`} role="status">
            <p>{applied ? "Import applied." : "Import stopped before the file was finished."}</p>
            <p>
                Saved document types: {savedTypes.length ? savedTypes.join(", ") : "none"}. Saved fields:{" "}
                {savedFields.length ? savedFields.join(", ") : "none"}.
            </p>
            {failed ? (
                <p role="alert">
                    {failedKind}
                    {failedKey ? ` ${failedKey}` : ""}
                    {failedMessage ? `: ${failedMessage}` : ""}
                </p>
            ) : null}
            {skippedTypes.length || skippedFields.length ? (
                <p>
                    Not attempted: document types {skippedTypes.length ? skippedTypes.join(", ") : "none"}; fields{" "}
                    {skippedFields.length ? skippedFields.join(", ") : "none"}.
                </p>
            ) : null}
            {applied ? null : <p>Choose the file again to preview what is still outstanding.</p>}
        </div>
    );
};

const ImportFieldsDialog = ({ onClose, onApplied }) => {
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState("");
    const [preview, setPreview] = useState(null);
    const [documentBody, setDocumentBody] = useState(null);
    const [result, setResult] = useState(null);
    const [errorItems, setErrorItems] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState("");

    const showError = (error, fallback) => {
        const details = importErrorDetails(error);
        setErrorItems(details.items);
        setErrorMessage(details.items.length ? "" : details.message || fallback);
    };

    const readFile = async (event) => {
        const file = event.target.files?.[0];
        setFileName(file?.name || "");
        setPreview(null);
        setResult(null);
        setDocumentBody(null);
        setErrorItems([]);
        setErrorMessage("");
        if (!file) return;
        setBusy(true);
        setPhase("preview");
        try {
            const parsed = JSON.parse(await readFileText(file));
            const nextPreview = await previewFieldImport(parsed);
            setDocumentBody(parsed);
            setPreview(nextPreview);
        } catch (readError) {
            showError(readError, "The catalog file could not be previewed.");
        } finally {
            setBusy(false);
            setPhase("");
        }
    };

    const apply = async () => {
        setBusy(true);
        setPhase("apply");
        setErrorItems([]);
        setErrorMessage("");
        try {
            const applied = await applyFieldImport(documentBody);
            setResult(applied);
            setPreview(null);
            if (applied.status === "applied" || applied.committed.fields.length || applied.committed.documentTypes.length) {
                await onApplied();
            }
        } catch (applyError) {
            showError(applyError, "The catalog was not imported.");
        } finally {
            setBusy(false);
            setPhase("");
        }
    };

    return (
        <Modal isOpen className="modal-content-compact cdes-import-modal" onClose={busy ? undefined : onClose}>
            <div className="cdes-client-form cdes-field-form">
                <div className="cdes-field-form__body">
                <p className="title">Import field catalog</p>
                <div className="form-input">
                    <label htmlFor="cdes-field-import-file">Catalog file</label>
                    <div className="cdes-import__file">
                        <button
                            type="button"
                            className="edit-btn cdes-import__file-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={busy}
                        >
                            Choose file
                        </button>
                        <span className="cdes-import__file-name">{fileName || "No file selected"}</span>
                    </div>
                    <input
                        id="cdes-field-import-file"
                        ref={fileInputRef}
                        className="cdes-import__file-input"
                        type="file"
                        accept="application/json,.json"
                        aria-label="Catalog file"
                        onChange={readFile}
                        disabled={busy}
                    />
                </div>
                {busy && phase === "preview" ? (
                    <p className="cdes-import__status" role="status">
                        <span className="loader loader--inline" />
                        Previewing the catalog…
                    </p>
                ) : null}
                {busy && phase === "apply" ? (
                    <p className="cdes-import__status" role="status">
                        <span className="loader loader--inline" />
                        Applying the import…
                    </p>
                ) : null}
                {preview ? <ImportPreview preview={preview} /> : null}
                {result ? <ImportResult result={result} /> : null}
                {errorItems.length ? (
                    <div className="cdes-import__alert" role="alert">
                        <p>The catalog was not imported.</p>
                        <ul>
                            {errorItems.map((item, index) => (
                                <li key={`${item}-${index}`}>{item}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}
                {errorMessage ? (
                    <p className="danger cdes-client-form__error" role="alert">
                        {errorMessage}
                    </p>
                ) : null}
                </div>
                <div className="cdes-field-form__footer">
                    <button type="button" className="outline-btn" onClick={onClose} disabled={busy}>
                        Close
                    </button>
                    <button type="button" className="btn" onClick={apply} disabled={busy || !preview}>
                        {busy && phase === "apply" ? "Applying…" : "Apply import"}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ImportFieldsDialog;
